import React, { useMemo, useState } from 'react';
import { adminApi, clearApiLogs, getApiLogs, healthApi, qaApi } from '../api/apiClient';

export default function ApiDiagnosticsPage() {
  const [workspaceId, setWorkspaceId] = useState('4');
  const [question, setQuestion] = useState('diagnostic ask test');
  const [logs, setLogs] = useState(() => getApiLogs());
  const [busy, setBusy] = useState(false);
  const [serverLogLines, setServerLogLines] = useState([]);
  const [serverLogMeta, setServerLogMeta] = useState(null);
  const [serverLogFilter, setServerLogFilter] = useState('');
  const [serverLogLimit, setServerLogLimit] = useState(2000);
  const [serverLogsBusy, setServerLogsBusy] = useState(false);

  React.useEffect(() => {
    const onLog = () => setLogs(getApiLogs());
    window.addEventListener('ventus-api-log', onLog);
    return () => window.removeEventListener('ventus-api-log', onLog);
  }, []);

  const compact = useMemo(
    () =>
      logs.map((x, idx) => ({
        key: `${x.at}-${idx}`,
        time: new Date(x.at).toLocaleTimeString(),
        stage: x.stage || 'response',
        method: x.method,
        url: x.url,
        status: x.status,
        message: x.message,
        ok: x.ok,
        authHeaderPresent: x.authHeaderPresent,
        authRetry: x.authRetry,
        refreshAttempted: x.refreshAttempted,
        refreshSucceeded: x.refreshSucceeded,
        tokenMeta: x.tokenMeta,
        data: x.data,
      })),
    [logs]
  );

  const run = async (fn) => {
    setBusy(true);
    try {
      await fn();
    } finally {
      setBusy(false);
      setLogs(getApiLogs());
    }
  };

  const loadServerLogs = async () => {
    setServerLogsBusy(true);
    try {
      const res = await adminApi.serverLogs(serverLogLimit, serverLogFilter.trim() || undefined);
      const data = res.data || {};
      setServerLogLines(Array.isArray(data.lines) ? data.lines : []);
      setServerLogMeta({
        bufferedLineCount: data.bufferedLineCount,
        returned: data.returned,
        note: data.note,
        filter: data.filter,
      });
    } catch (e) {
      setServerLogLines([`Fout: ${e?.response?.status || ''} ${e?.message || e}`]);
      setServerLogMeta(null);
    } finally {
      setServerLogsBusy(false);
    }
  };

  return (
    <div style={{ padding: 20 }}>
      <h2>API Diagnostics</h2>
      <p>Use this page to inspect what the API returns (auth/access/AI errors).</p>

      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        <button type="button" className="v-btn v-btn--ghost" disabled={busy} onClick={() => run(() => healthApi.get())}>
          Test /health
        </button>
        <button type="button" className="v-btn v-btn--ghost" disabled={busy} onClick={() => run(() => window.fetch('/api/admin/token-debug', {
          headers: { Authorization: `Bearer ${localStorage.getItem('ventus_token') || ''}` },
        }).then(async (res) => {
          const data = await res.json().catch(() => ({}));
          const event = new CustomEvent('ventus-api-log', {
            detail: {
              at: new Date().toISOString(),
              ok: res.ok,
              stage: 'response',
              method: 'GET',
              url: '/admin/token-debug',
              status: res.status,
              message: data?.reason || data?.message || null,
              authHeaderPresent: true,
              authRetry: false,
              data,
            },
          });
          window.dispatchEvent(event);
          const current = window.__ventusApiLogs || [];
          window.__ventusApiLogs = [event.detail, ...current].slice(0, 200);
        }))}>
          Test /admin/token-debug
        </button>
        <button
          type="button"
          className="v-btn v-btn--ghost"
          disabled={busy}
          onClick={() => run(() => qaApi.list(workspaceId))}
        >
          Test GET /workspaces/{'{id}'}/qa
        </button>
        <button
          type="button"
          className="v-btn v-btn--primary"
          disabled={busy}
          onClick={() => run(() => qaApi.ask(workspaceId, question))}
        >
          Test POST ask
        </button>
        <button
          type="button"
          className="v-btn v-btn--ghost"
          onClick={() => {
            clearApiLogs();
            setLogs([]);
          }}
        >
          Clear log
        </button>
      </div>

      <section style={{ marginBottom: 24, padding: 16, background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
        <h3 style={{ marginTop: 0 }}>Serverlogs (tijdelijk, admin)</h3>
        <p style={{ marginTop: 0, color: '#475569', fontSize: 14 }}>
          Recente JVM-logregels sinds backend-start (Logback root → geheugen). Alleen voor admins; zet op de server{' '}
          <code>VENTUS_ADMIN_SERVER_LOG_BUFFER=false</code> om uit te zetten.
        </p>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10, alignItems: 'center' }}>
          <input
            type="number"
            min={100}
            max={5000}
            value={serverLogLimit}
            onChange={(e) => setServerLogLimit(Number(e.target.value) || 2000)}
            style={{ padding: 8, width: 100 }}
            title="Max regels"
          />
          <input
            value={serverLogFilter}
            onChange={(e) => setServerLogFilter(e.target.value)}
            placeholder="filter (substring)"
            style={{ padding: 8, minWidth: 220 }}
          />
          <button type="button" className="v-btn v-btn--primary" disabled={serverLogsBusy} onClick={loadServerLogs}>
            {serverLogsBusy ? 'Laden…' : 'Haal serverlogs op'}
          </button>
        </div>
        {serverLogMeta ? (
          <div style={{ fontSize: 13, color: '#64748b', marginBottom: 8 }}>
            buffer: <strong>{serverLogMeta.bufferedLineCount}</strong> regels | getoond:{' '}
            <strong>{serverLogMeta.returned}</strong>
            {serverLogMeta.note ? <div style={{ marginTop: 4 }}>{serverLogMeta.note}</div> : null}
          </div>
        ) : null}
        <pre
          style={{
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            maxHeight: '40vh',
            overflow: 'auto',
            margin: 0,
            padding: 12,
            background: '#0f172a',
            color: '#e2e8f0',
            borderRadius: 6,
            fontSize: 12,
            lineHeight: 1.4,
          }}
        >
          {serverLogLines.length ? serverLogLines.join('\n') : 'Nog geen logs geladen — klik op «Haal serverlogs op».'}
        </pre>
      </section>

      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        <input
          value={workspaceId}
          onChange={(e) => setWorkspaceId(e.target.value)}
          placeholder="workspace id"
          style={{ padding: 8, minWidth: 140 }}
        />
        <input
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          placeholder="question for ask test"
          style={{ padding: 8, minWidth: 360 }}
        />
      </div>

      <div style={{ border: '1px solid #ddd', borderRadius: 8, padding: 12, maxHeight: '65vh', overflow: 'auto' }}>
        {compact.length === 0 ? (
          <div>No API events yet.</div>
        ) : (
          compact.map((e) => (
            <div key={e.key} style={{ borderBottom: '1px solid #eee', padding: '10px 0' }}>
              <div>
                <strong>{e.time}</strong> | <strong>{e.stage}</strong> | <strong>{e.method}</strong> <code>{e.url}</code> | status:{' '}
                <strong style={{ color: e.ok ? '#15803d' : '#b91c1c' }}>{String(e.status ?? '-')}</strong>
              </div>
              <div>
                authHeader: <strong>{String(e.authHeaderPresent)}</strong> | retry:{' '}
                <strong>{String(e.authRetry)}</strong>
                {e.stage === 'request' ? (
                  <>
                    {' '}| refreshAttempted: <strong>{String(e.refreshAttempted)}</strong> | refreshSucceeded:{' '}
                    <strong>{String(e.refreshSucceeded)}</strong>
                  </>
                ) : null}
                {e.tokenMeta ? (
                  <>
                    {' '}| tokenLen: <strong>{String(e.tokenMeta.accessLength ?? 0)}</strong>
                    {e.tokenMeta.accessParts != null ? (
                      <>
                        {' '}| tokenParts: <strong>{String(e.tokenMeta.accessParts)}</strong>
                      </>
                    ) : null}
                  </>
                ) : null}
              </div>
              {e.message ? <div>message: {e.message}</div> : null}
              {e.data != null ? (
                <pre style={{ whiteSpace: 'pre-wrap', marginTop: 6 }}>
                  {JSON.stringify(e.data, null, 2)}
                </pre>
              ) : null}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
